import { type Module, modules, scenes, tasks } from "@history-sim/content";
import type { LearningEvent } from "@history-sim/domain";

export type FirstAttemptStatus = "correct" | "needs_review" | "not_recorded";
export type ReportDetailScope = "full_local_session" | "server_snapshot_plus_current";

export interface ReportSceneProgress {
  readonly completedNodeIds?: readonly string[];
  readonly taskAttemptCounts?: Readonly<Record<string, number>>;
  readonly remediation?: {
    readonly sourceTaskId: string;
    readonly required: boolean;
    readonly completed: boolean;
  };
}

export interface LearningReportProgress {
  readonly completedSceneIds: readonly string[];
  readonly completedTaskIds: readonly string[];
  readonly completedMomentIds: readonly string[];
  readonly sceneProgressById: Readonly<Record<string, ReportSceneProgress | undefined>>;
  readonly events: readonly LearningEvent[];
}

export interface TaskProgressReport {
  readonly taskId: string;
  readonly title: string;
  readonly sceneId: string;
  readonly attempts: number;
  readonly passed: boolean;
  readonly firstAttempt: FirstAttemptStatus;
  readonly remediationStarted: boolean;
  readonly remediationCompleted: boolean;
}

export interface ModuleProgressReport {
  readonly module: Module;
  readonly detailScope: ReportDetailScope;
  readonly sceneTitles: readonly string[];
  readonly completedSceneCount: number;
  readonly sceneCount: number;
  readonly taskReports: readonly TaskProgressReport[];
  readonly passedTaskCount: number;
  readonly taskCount: number;
  readonly attemptCount: number;
  readonly firstAttemptCorrectCount: number;
  readonly retriedTaskCount: number;
  readonly retryCount: number;
  readonly remediationStartedCount: number;
  readonly remediationCompletedCount: number;
  readonly sourceViewCount: number;
  readonly contentViewCount: number;
  readonly completed: boolean;
}

export interface CourseProgressReport {
  readonly modules: readonly ModuleProgressReport[];
  readonly detailScope: ReportDetailScope;
  readonly completedModuleCount: number;
  readonly completedSceneCount: number;
  readonly sceneCount: number;
  readonly passedTaskCount: number;
  readonly taskCount: number;
  readonly attemptCount: number;
  readonly firstAttemptCorrectCount: number;
  readonly retriedTaskCount: number;
  readonly retryCount: number;
  readonly remediationStartedCount: number;
  readonly remediationCompletedCount: number;
  readonly sourceViewCount: number;
  readonly contentViewCount: number;
  readonly completed: boolean;
}

function moduleById(moduleId: string): Module {
  const module = modules.find((candidate) => candidate.id === moduleId);
  if (!module) throw new Error(`Unknown learning module: ${moduleId}`);
  return module;
}

function moduleEventSet(module: Module): Set<string> {
  return new Set(module.sceneIds);
}

function eventAttemptCount(events: readonly LearningEvent[], taskId: string): number {
  return events.filter((event) => event.type === "task_answered" && event.taskId === taskId).length;
}

function taskProgress(
  taskId: string,
  sceneId: string,
  title: string,
  progress: LearningReportProgress,
): TaskProgressReport {
  const taskEvents = progress.events.filter(
    (event) => event.type === "task_answered" && event.taskId === taskId,
  );
  const firstCorrect = taskEvents[0]?.payload.correct;
  const attempts = Math.max(
    progress.sceneProgressById[sceneId]?.taskAttemptCounts?.[taskId] ?? 0,
    eventAttemptCount(progress.events, taskId),
  );
  const passed = progress.completedTaskIds.includes(taskId);
  const firstAttempt: FirstAttemptStatus =
    typeof firstCorrect === "boolean"
      ? firstCorrect
        ? "correct"
        : "needs_review"
      : attempts === 0
        ? "not_recorded"
        : passed && attempts === 1
          ? "correct"
          : "needs_review";
  const sceneRemediation = progress.sceneProgressById[sceneId]?.remediation;
  const remediationStarted =
    progress.events.some(
      (event) => event.type === "remediation_started" && event.taskId === taskId,
    ) ||
    (sceneRemediation?.sourceTaskId === taskId && sceneRemediation.required);
  const remediationCompleted =
    progress.events.some(
      (event) => event.type === "remediation_completed" && event.taskId === taskId,
    ) ||
    (sceneRemediation?.sourceTaskId === taskId && sceneRemediation.completed);

  return {
    taskId,
    title,
    sceneId,
    attempts,
    passed,
    firstAttempt,
    remediationStarted,
    remediationCompleted,
  };
}

export function moduleForScene(sceneId: string): Module | undefined {
  const scene = scenes.find((candidate) => candidate.id === sceneId);
  return scene ? modules.find((module) => module.id === scene.moduleId) : undefined;
}

export function isFinalSceneInModule(sceneId: string): boolean {
  const module = moduleForScene(sceneId);
  return module?.sceneIds.at(-1) === sceneId;
}

export function completedModuleReportIdAfterScene(
  sceneId: string,
  completedSceneIds: readonly string[],
): string | undefined {
  const module = moduleForScene(sceneId);
  if (!module) return undefined;
  return module.sceneIds.every((candidate) => completedSceneIds.includes(candidate))
    ? module.id
    : undefined;
}

export function buildModuleProgressReport(
  moduleId: string,
  progress: LearningReportProgress,
): ModuleProgressReport {
  const module = moduleById(moduleId);
  const moduleSceneIds = moduleEventSet(module);
  const moduleScenes = scenes.filter((scene) => moduleSceneIds.has(scene.id));
  const moduleTasks = tasks.filter((task) => moduleSceneIds.has(task.sceneId));
  const taskReports = moduleTasks.map((task) =>
    taskProgress(task.id, task.sceneId, task.title, progress),
  );
  const completedSceneCount = module.sceneIds.filter((sceneId) =>
    progress.completedSceneIds.includes(sceneId),
  ).length;
  const moduleEvents = progress.events.filter(
    (event) => event.sceneId !== undefined && moduleSceneIds.has(event.sceneId),
  );
  const recordedContentViews = moduleEvents.filter(
    (event) => event.type === "content_viewed",
  ).length;
  const detailScope: ReportDetailScope = progress.events.some(
    (event) => event.type === "session_started" && event.payload.mode === "server_restore",
  )
    ? "server_snapshot_plus_current"
    : "full_local_session";

  return {
    module,
    detailScope,
    sceneTitles: moduleScenes.map((scene) => scene.title),
    completedSceneCount,
    sceneCount: module.sceneIds.length,
    taskReports,
    passedTaskCount: taskReports.filter((task) => task.passed).length,
    taskCount: taskReports.length,
    attemptCount: taskReports.reduce((total, task) => total + task.attempts, 0),
    firstAttemptCorrectCount: taskReports.filter((task) => task.firstAttempt === "correct").length,
    retriedTaskCount: taskReports.filter((task) => task.attempts > 1).length,
    retryCount: taskReports.reduce((total, task) => total + Math.max(0, task.attempts - 1), 0),
    remediationStartedCount: taskReports.filter((task) => task.remediationStarted).length,
    remediationCompletedCount: taskReports.filter((task) => task.remediationCompleted).length,
    sourceViewCount: moduleEvents.filter((event) =>
      ["archive_opened", "source_viewed"].includes(event.type),
    ).length,
    contentViewCount:
      recordedContentViews > 0
        ? recordedContentViews
        : module.id === "M1"
          ? progress.completedMomentIds.length
          : 0,
    completed: completedSceneCount === module.sceneIds.length,
  };
}

export function buildCourseProgressReport(progress: LearningReportProgress): CourseProgressReport {
  const moduleReports = modules.map((module) => buildModuleProgressReport(module.id, progress));
  const sum = (selector: (report: ModuleProgressReport) => number) =>
    moduleReports.reduce((total, report) => total + selector(report), 0);

  return {
    modules: moduleReports,
    detailScope: moduleReports.some(
      (report) => report.detailScope === "server_snapshot_plus_current",
    )
      ? "server_snapshot_plus_current"
      : "full_local_session",
    completedModuleCount: moduleReports.filter((report) => report.completed).length,
    completedSceneCount: sum((report) => report.completedSceneCount),
    sceneCount: sum((report) => report.sceneCount),
    passedTaskCount: sum((report) => report.passedTaskCount),
    taskCount: sum((report) => report.taskCount),
    attemptCount: sum((report) => report.attemptCount),
    firstAttemptCorrectCount: sum((report) => report.firstAttemptCorrectCount),
    retriedTaskCount: sum((report) => report.retriedTaskCount),
    retryCount: sum((report) => report.retryCount),
    remediationStartedCount: sum((report) => report.remediationStartedCount),
    remediationCompletedCount: sum((report) => report.remediationCompletedCount),
    sourceViewCount: sum((report) => report.sourceViewCount),
    contentViewCount: sum((report) => report.contentViewCount),
    completed: moduleReports.every((report) => report.completed),
  };
}
